import { Box, styled } from '@mui/material'
import { Typography } from '@carto/meridian-ds/components'
import { PostHelpQuestionResponse } from '../api/helpPanel'
import { FormattedMessage } from 'react-intl'
import Loader from 'features/common/ui/utils/Loader'
import DocLink from 'features/common/ui/utils/DocLink'
import MarkDownPreviewMode from 'features/common/ui/MarkDownEditor/MarkDownPreviewMode'

const PanelContent = styled(Box)(({ theme }) => ({
  paddingRight: theme.spacing(2)
}))

const MarkDownPreviewModeStyled = styled(Box)(({ theme }) => ({
  '& .MuiTypography-root': {
    color: `${theme.palette.text.secondary} !important`
  },
  '& ul, ol': {
    color: theme.palette.text.secondary
  }
}))

const AnswerWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2.25)
}))

const NoResultsWrapper = styled(Box)(({ theme }) => ({
  padding: theme.spacing(0, 1.5, 0, 1.5),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2)
}))

const DocLinkStyled = styled(DocLink)(({ theme }) => ({
  width: 'fit-content',
  '& .MuiTypography-root': {
    color: `${theme.palette.primary.main} !important`
  }
}))

const SecondaryTypography = styled(Typography)(({ theme }) => ({ color: `${theme.palette.text.secondary} !important` }))

const LoaderWrapper = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
  '& .MuiTypography-root': {
    color: `${theme.palette.text.secondary} !important`
  }
}))

const InformationGatheredWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'baseline',
  flexDirection: 'column',
  paddingRight: theme.spacing(2),
  [theme.breakpoints.up('sm')]: {
    flexDirection: 'row'
  }
}))

export default function QuestionAnswer({
  question,
  answer,
  isLoading,
  isError
}: {
  question: string
  answer: PostHelpQuestionResponse | null | undefined
  isLoading: boolean
  isError: boolean
}) {
  if (isLoading) {
    return (
      <LoaderWrapper>
        <Loader labelId="helpPanel.questionPanel.loader" />
      </LoaderWrapper>
    )
  } else if (answer === null || isError) {
    return (
      <NoResultsWrapper>
        <SecondaryTypography variant="body2">
          <FormattedMessage id="helpPanel.questionPanel.noResults" />
        </SecondaryTypography>
        <DocLinkStyled useButton>
          <FormattedMessage id="helpPanel.questionPanel.visitOurDocumentation" />
        </DocLinkStyled>
      </NoResultsWrapper>
    )
  } else if (answer !== undefined) {
    const sourceUrl = new URL(answer.sources[0])
    const overrideComponents = {
      h3: (node) => {
        return (
          <Typography color="inherit" variant="subtitle2" mb={1}>
            {node.children}
          </Typography>
        )
      }
    }
    return (
      <AnswerWrapper>
        <InformationGatheredWrapper flexDirection={{ xs: 'column', sm: 'row' }}>
          <SecondaryTypography variant="caption">
            <FormattedMessage id="helpPanel.questionPanel.informationGathered" />
          </SecondaryTypography>
          <DocLinkStyled size="small" docUrl={sourceUrl.pathname} useButton>
            <FormattedMessage id="helpPanel.questionPanel.learnMore" />
          </DocLinkStyled>
        </InformationGatheredWrapper>
        <PanelContent>
          <Typography variant="body1" fontWeight="bold">
            {question}
          </Typography>
          <MarkDownPreviewModeStyled>
            <MarkDownPreviewMode
              content={answer.markdownText}
              baseTypographyVariant="body2"
              overrideComponents={overrideComponents}
            />
          </MarkDownPreviewModeStyled>
        </PanelContent>
      </AnswerWrapper>
    )
  } else {
    return null
  }
}
